import type { Invite } from "../net/api.js";

/**
 * The invite link a host created, kept for this tab so the room page can show it after a reload.
 * The api never stores the secret, so the link can't be fetched again; a new one can be made.
 */
const key = (roomId: string) => `obean:invite:${roomId}`;

export function rememberInvite(roomId: string, invite: Invite): void {
  try {
    sessionStorage.setItem(key(roomId), JSON.stringify(invite));
  } catch {
    // Storage can be off (private windows); the room page then makes a new link.
  }
}

/** Forgets every link this tab remembers (after "Delete my data"). */
export function forgetInvites(): void {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k?.startsWith("obean:invite:") || k?.startsWith("obean:follow:") || k === LEFT_KEY)
        sessionStorage.removeItem(k);
    }
  } catch {
    // No storage, so nothing to forget.
  }
}

export function recallInvite(roomId: string, now = Date.now()): Invite | null {
  try {
    const raw = sessionStorage.getItem(key(roomId));
    const invite = raw ? (JSON.parse(raw) as Invite) : null;
    return invite && invite.expiresAt > now ? invite : null;
  } catch {
    return null;
  }
}

/**
 * The game a new room should start with, when the host came from a game's "Play with friends"
 * (`/new?game=<id>`): kept for this tab until the room has picked it.
 */
const gameKey = (roomId: string) => `obean:room-game:${roomId}`;

export function rememberRoomGame(roomId: string, gameId: string): void {
  try {
    sessionStorage.setItem(gameKey(roomId), gameId);
  } catch {
    // No storage: the room starts with its default game, and the host can switch.
  }
}

/** The game this new room should start with, once: asking forgets it. */
export function takeRoomGame(roomId: string): string | null {
  try {
    const game = sessionStorage.getItem(gameKey(roomId));
    sessionStorage.removeItem(gameKey(roomId));
    return game;
  } catch {
    return null;
  }
}

/**
 * The room this tab just left (#616), so the start page can offer the way back while the room may
 * still be open (it closes once it has been empty for ten minutes).
 */
const LEFT_KEY = "obean:left";
export const LEFT_SHOWN_MS = 10 * 60_000;

export function rememberLeft(roomId: string, now = Date.now()): void {
  try {
    sessionStorage.setItem(LEFT_KEY, JSON.stringify({ roomId, at: now }));
  } catch {
    // No storage: the browser's back button still goes back to the room.
  }
}

export function recallLeft(now = Date.now()): string | null {
  try {
    const raw = sessionStorage.getItem(LEFT_KEY);
    const left = raw ? (JSON.parse(raw) as { roomId: string; at: number }) : null;
    return left && now - left.at < LEFT_SHOWN_MS ? left.roomId : null;
  } catch {
    return null;
  }
}

export function forgetLeft(): void {
  try {
    sessionStorage.removeItem(LEFT_KEY);
  } catch {
    // Nothing to forget.
  }
}

/**
 * Following a friend into the new room they made (#616): the invite page joins at once with the
 * name this browser already has, instead of asking. Asking forgets it.
 */
const followKey = (roomId: string) => `obean:follow:${roomId}`;

export function rememberFollow(roomId: string): void {
  try {
    sessionStorage.setItem(followKey(roomId), "1");
  } catch {
    // No storage: the invite page asks for the name, one more tap.
  }
}

export function takeFollow(roomId: string): boolean {
  try {
    const follow = sessionStorage.getItem(followKey(roomId)) === "1";
    sessionStorage.removeItem(followKey(roomId));
    return follow;
  } catch {
    return false;
  }
}
